import ArrowCircleDown from '@mui/icons-material/ArrowCircleDown';
import Check from '@mui/icons-material/Check';
import { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  InvoiceState,
  InvoiceStateEvent,
  putV1CompaniesCompanyIdInvoicesInvoiceIdApprove,
  putV1CompaniesCompanyIdInvoicesInvoiceIdUnapprove,
  RateType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';

import { Chip } from '~components/Chip/Chip';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import Menu, { MenuHandler } from '~components/Menu/Menu';
import {
  LoadingSpinner,
  OverflowAwareText,
} from '~components/Order/ordersDispatchStyledComponents';
import { FeatureFlags } from '~constants/featureFlags';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { InvoiceBasic } from '~hooks/useInvoices/models';
import { JobInvoicing } from '~hooks/useJob/models';
import {
  getFormattedRateValue,
  getRateTypeLabel,
} from '~pages/Settings/RatesManagement/rateUtils';
import connection from '~services/connectionModule';
import { useStores } from '~store/RootStore';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import JobsQueryContext from '../../OrdersList/ExpandedBlock/JobsQueryContext';
import { SmallIconButton } from '../../TicketDrawer/styled';

const invoiceApprovedStates = [InvoiceState.APPROVED, InvoiceState.CUSTOMER_APPROVED];

interface InvoiceColumnProps {
  invoiceType: 'payable' | 'receivable';
  job: JobInvoicing;
  onJobUpdated?: (job: JobInvoicing) => void;
  sx?: SxProps<Theme>;
}

export default function InvoiceColumn({
  invoiceType,
  job,
  onJobUpdated,
  sx,
}: InvoiceColumnProps) {
  const isPayableInvoice = invoiceType === 'payable';
  const invoice = isPayableInvoice ? job.payableInvoice : job.receivableInvoice;

  const { updateJobInQueryData } = useContext(JobsQueryContext);
  const { currencyFormatter } = useCompanyCurrency();
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  // Under load-level pricing, un-approving an invoice RETAINS its approved rates (re-flowing only on
  // a later rate-card change) instead of refreshing to the latest, so the sync-warning copy differs.
  const isLoadLevelPricingOn = useFeatureFlag({
    featureFlagKey: FeatureFlags.loadLevelPricing,
  });

  const [isBusy, setIsBusy] = useState(false);
  const [invoiceToUnapprove, setInvoiceToUnapprove] = useState<InvoiceBasic>();

  const approvalAction = useMemo(() => {
    if (!invoice?.state) {
      return null;
    }

    const isUnapprovable = invoice.stateEvents.includes(InvoiceStateEvent.UNAPPROVE);

    if (isUnapprovable) {
      return 'unapprove';
    }

    const isApprovable = invoice.stateEvents.includes(InvoiceStateEvent.APPROVE);

    return isApprovable ? InvoiceStateEvent.APPROVE : null;
  }, [invoice?.state, invoice?.stateEvents]);

  const isApprovedReadonly = useMemo(() => {
    if (!invoice?.state) {
      return false;
    }

    return (
      invoiceApprovedStates.includes(invoice.state) &&
      !invoice.stateEvents.includes(InvoiceStateEvent.UNAPPROVE)
    );
  }, [invoice?.state, invoice?.stateEvents]);

  const approveInvoice = useCallback(
    async (invoice: InvoiceBasic) => {
      let updatedInvoice: InvoiceBasic | null = null;

      try {
        const response = await putV1CompaniesCompanyIdInvoicesInvoiceIdApprove({
          path: { 'company-id': companyId, 'invoice-id': invoice.id },
        });

        updatedInvoice = InvoiceBasic.parse(response.data.data);
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.invoices.failed_to_approve'),
        );
      }

      return updatedInvoice;
    },
    [companyId],
  );

  const unapproveInvoice = useCallback(
    async (invoice: InvoiceBasic) => {
      let updatedInvoice: InvoiceBasic | null = null;

      try {
        const response = await putV1CompaniesCompanyIdInvoicesInvoiceIdUnapprove({
          path: { 'company-id': companyId, 'invoice-id': invoice.id },
        });

        updatedInvoice = InvoiceBasic.parse(response.data.data);
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.invoices.failed_to_unapprove'),
        );
      }

      return updatedInvoice;
    },
    [companyId],
  );

  const handleConfirmUnapproveInvoiceClick = useCallback(async () => {
    if (!invoiceToUnapprove) {
      return;
    }

    try {
      setIsBusy(true);

      const updatedInvoice = await unapproveInvoice(invoiceToUnapprove);

      if (updatedInvoice) {
        const payableInvoice = isPayableInvoice ? updatedInvoice : job.payableInvoice;
        const receivableInvoice = isPayableInvoice
          ? job.receivableInvoice
          : updatedInvoice;

        const updatedJob = JobInvoicing.fromJobOrJobInvoicingAndInvoices(
          job,
          receivableInvoice,
          payableInvoice,
        );

        if (onJobUpdated) {
          onJobUpdated(updatedJob);
        } else {
          updateJobInQueryData(updatedJob);
        }
      }
    } finally {
      setIsBusy(false);
      setInvoiceToUnapprove(undefined);
    }
  }, [invoiceToUnapprove, isPayableInvoice, job, onJobUpdated, unapproveInvoice]);

  const handleApprovalActionClick = useCallback(async () => {
    if (!invoice || !approvalAction) {
      return;
    }

    const isUnapproveAction = approvalAction === 'unapprove';

    if (isUnapproveAction) {
      setInvoiceToUnapprove(invoice);

      return;
    }

    setIsBusy(true);

    const updatedInvoice = await approveInvoice(invoice);

    if (updatedInvoice) {
      const payableInvoice = isPayableInvoice ? updatedInvoice : job.payableInvoice;
      const receivableInvoice = isPayableInvoice ? job.receivableInvoice : updatedInvoice;

      const updatedJob = JobInvoicing.fromJobOrJobInvoicingAndInvoices(
        job,
        receivableInvoice,
        payableInvoice,
      );

      if (onJobUpdated) {
        onJobUpdated(updatedJob);
      } else {
        updateJobInQueryData(updatedJob);
      }
    }

    setIsBusy(false);
  }, [approvalAction, invoice, isPayableInvoice, job, onJobUpdated, approveInvoice]);

  if (!invoice) {
    return (
      <Box sx={sx}>
        <OverflowAwareText>-</OverflowAwareText>
      </Box>
    );
  }

  return (
    <Box sx={sx}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <OverflowAwareText>
            <Typography
              component="span"
              variant="body2"
              sx={{
                fontWeight: 700,
              }}
            >
              {currencyFormatter(invoice.totalAmount)}
            </Typography>
          </OverflowAwareText>

          <OverflowAwareText>
            {getFormattedRateValue(
              invoice.rateType ?? RateType.RATE_PER_HOUR,
              invoice.rateValue ?? 0,
              currencyFormatter,
            )}{' '}
            {invoice.rateType ? getRateTypeLabel(invoice.rateType) : ''}
          </OverflowAwareText>

          {invoice.addOnChargesCount > 0 && (
            <>
              <OverflowAwareText pt={0.5}>{`${t('common.add_ons')}:`}</OverflowAwareText>

              <OverflowAwareText>
                {`${currencyFormatter(invoice.addOnChargesTotalAmount)} (${invoice.addOnChargesCount})`}
              </OverflowAwareText>
            </>
          )}
        </Box>

        {approvalAction && (
          <Box sx={{ flex: 0, minWidth: '40px', textAlign: 'right' }}>
            <ApprovalActionButton
              action={approvalAction}
              isBusy={isBusy}
              onClick={handleApprovalActionClick}
            />
          </Box>
        )}

        {!approvalAction && isApprovedReadonly && (
          <Box sx={{ flex: 0, minWidth: '40px', textAlign: 'right' }}>
            <ReadonlyApprovedIndicator />
          </Box>
        )}
      </Box>
      <ConfirmationDialog
        confirmButtonProps={{
          children: t('approvals.approve_jobs.unapprove_invoice_dialog.confirm_button'),
        }}
        isBusy={isBusy}
        isOpen={Boolean(invoiceToUnapprove)}
        onClose={() => setInvoiceToUnapprove(undefined)}
        onConfirm={handleConfirmUnapproveInvoiceClick}
        title={t('approvals.approve_jobs.unapprove_invoice_dialog.title')}
      >
        <Typography>
          {t('approvals.approve_jobs.unapprove_invoice_dialog.confirm_prompt')}
        </Typography>

        <Typography
          sx={{
            mt: 2,
          }}
        >
          {t(
            isLoadLevelPricingOn
              ? 'approvals.approve_jobs.unapprove_invoice_dialog.sync_warning_rates_retained'
              : 'approvals.approve_jobs.unapprove_invoice_dialog.sync_warning',
          )}
        </Typography>
      </ConfirmationDialog>
    </Box>
  );
}

interface ApprovalActionButtonProps {
  action: 'approve' | 'unapprove';
  isBusy?: boolean;
  onClick: () => void;
}

function ApprovalActionButton({ action, isBusy, onClick }: ApprovalActionButtonProps) {
  const isApproveAction = action === 'approve';

  const menuHandler = useRef<MenuHandler>(null);

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onClick();
    },
    [onClick],
  );

  if (isApproveAction) {
    return (
      <Tooltip title={t('approvals.driver_day.approve')}>
        <span>
          <ApproveInvoiceCTA isBusy={isBusy} onClick={handleClick} />
        </span>
      </Tooltip>
    );
  }

  return (
    <Menu
      ref={menuHandler}
      menuTrigger={
        <span>
          <UnapproveInvoiceMenuTrigger />
        </span>
      }
    >
      <UnapproveInvoiceMenuItem
        onClick={() => {
          menuHandler.current?.onClose?.();
          onClick();
        }}
      />
    </Menu>
  );
}

interface ApproveInvoiceCTAProps {
  isBusy?: boolean;
  onClick: (event: React.MouseEvent) => void;
}

function ApproveInvoiceCTA({ isBusy, onClick }: ApproveInvoiceCTAProps) {
  return (
    <SmallIconButton
      disabled={isBusy}
      onClick={onClick}
      sx={(theme) => ({
        borderStyle: 'dashed !important',
        color: `${theme.brandV2.colors.treadGray5} !important`,
        height: '25px !important',
        width: '25px !important',
        ...(isBusy ? { pointerEvents: 'none !important' } : {}),
        '&:hover': {
          borderColor: `${theme.brandV2.colors.treadGray1} !important`,
          color: `${theme.brandV2.colors.treadGray1} !important`,
        },
      })}
    >
      {isBusy ? (
        <span>
          <LoadingSpinner
            isVisible
            loadingIndicatorColor="success"
            loadingIndicatorSize={15}
          />
        </span>
      ) : (
        <Check />
      )}
    </SmallIconButton>
  );
}

function UnapproveInvoiceMenuTrigger() {
  return (
    <Box
      data-test-id="unapprove-invoice-menu-trigger"
      sx={{ cursor: 'pointer', display: 'inline-block' }}
    >
      <Chip color="success" label={<Check sx={{ fontSize: '15px' }} />} />
    </Box>
  );
}

function ReadonlyApprovedIndicator() {
  return (
    <Tooltip title={t('approvals.driver_day.approved')}>
      <Box
        aria-label={t('approvals.driver_day.approved')}
        data-test-id="readonly-approved-indicator"
        role="status"
        sx={{ cursor: 'default', display: 'inline-block' }}
      >
        <Chip
          color="success"
          hideDropdownArrow
          label={<Check sx={{ fontSize: '15px' }} />}
        />
      </Box>
    </Tooltip>
  );
}

interface UnapproveInvoiceMenuItemProps {
  onClick: () => void;
}

function UnapproveInvoiceMenuItem({ onClick }: UnapproveInvoiceMenuItemProps) {
  return (
    <MenuItem
      data-test-id="unapprove-invoice-menu-item"
      onClick={(event: React.MouseEvent) => {
        event.stopPropagation();
        onClick();
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: 1,
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <ArrowCircleDown
          sx={(theme) => ({
            color: theme.brandV2.colors.treadGray3,
            fontSize: '15px',
            transform: 'rotate(90deg)',
          })}
        />

        <OverflowAwareText>{t('approvals.driver_day.unapprove')}</OverflowAwareText>
      </Box>
    </MenuItem>
  );
}
